import React, { useRef } from 'react';

/**
 * Product gallery: the large image and, when there is more than one image, arrows, a counter,
 * swipe (phone) and a row of thumbnails to switch between them.
 * The active image is kept by the page (activeImage / onImageChange).
 */
const ProductImages = ({ images, activeImage, onImageChange, alt }) => {
  const touchStartX = useRef(null);

  if (!images || images.length === 0) return null;

  const activeIndex = Math.max(0, images.indexOf(activeImage));
  const hasMany = images.length > 1;
  const go = (step) => onImageChange(images[(activeIndex + step + images.length) % images.length]);

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
  };

  const arrowClass = 'absolute top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/80 text-gray-800 shadow hover:bg-white';

  return (
    <div dir="ltr">
      <div
        className="relative overflow-hidden rounded-2xl border border-gray-100"
        onTouchStart={hasMany ? onTouchStart : undefined}
        onTouchEnd={hasMany ? onTouchEnd : undefined}
      >
        <img src={images[activeIndex]} alt={alt} className="h-full w-full select-none object-cover" draggable="false" />
        {hasMany && (
          <>
            <button type="button" onClick={() => go(-1)} aria-label="Image précédente" className={`${arrowClass} left-3`}>
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button type="button" onClick={() => go(1)} aria-label="Image suivante" className={`${arrowClass} right-3`}>
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <span className="absolute bottom-3 right-3 rounded-md bg-black/60 px-2 py-0.5 text-xs font-medium text-white">
              {activeIndex + 1} / {images.length}
            </span>
          </>
        )}
      </div>

      {hasMany && (
        <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
          {images.map((img, i) => (
            <button
              key={img}
              type="button"
              onClick={() => onImageChange(img)}
              aria-label={`Image ${i + 1}`}
              className={`h-16 w-16 flex-shrink-0 overflow-hidden rounded-lg border-2 transition-opacity ${
                i === activeIndex ? 'border-gray-900' : 'border-transparent opacity-70 hover:opacity-100'
              }`}
            >
              <img src={img} alt="" loading="lazy" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductImages;
